// Shared, client-safe constants and schemas for /submit. No server imports.

import { z } from "zod";

/** §6 / §5.5 — submission types. */
export const SUBMISSION_TYPES = ["victim", "confessor"] as const;
export type SubmissionType = (typeof SUBMISSION_TYPES)[number];

/** Submission languages — IMO six + extended candidates per §6. */
export const SUBMISSION_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ru", label: "Русский" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "zh", label: "中文" },
  { code: "ar", label: "العربية" },
  { code: "tl", label: "Tagalog" },
  { code: "hi", label: "हिन्दी" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "uk", label: "Українська" },
  { code: "pl", label: "Polski" },
  { code: "pt-BR", label: "Português (BR)" },
  { code: "el", label: "Ελληνικά" },
] as const;
export type SubmissionLanguage = (typeof SUBMISSION_LANGUAGES)[number]["code"];

/** §5.3 facets (all optional). */
export const FACETS = {
  abuser_position: ["master", "chief_officer", "chief_engineer", "other_officer", "shore_mgmt", "other"],
  victim_position: ["cadet", "rating", "junior_officer", "senior_officer", "catering", "other"],
  vessel_type: ["bulk", "tanker", "container", "general_cargo", "passenger", "offshore", "fishing", "other"],
  region: ["european_flag", "open_registry", "asia_pacific", "americas", "africa", "middle_east", "other"],
  outcome: ["endured", "left_vessel", "complaint_filed", "case_won", "case_lost", "unresolved"],
  decade: ["1980s", "1990s", "2000s", "2010s", "2020s"],
} as const;
export type FacetKey = keyof typeof FACETS;

/** §5.1 identity choices. */
export const IDENTITY_MODES = ["anonymous", "pseudonym", "pseudonym_contact"] as const;
export type IdentityMode = (typeof IDENTITY_MODES)[number];

const facetSchema = z
  .object({
    abuser_position: z.string().optional(),
    victim_position: z.string().optional(),
    vessel_type: z.string().optional(),
    region: z.string().optional(),
    outcome: z.string().optional(),
    decade: z.string().optional(),
  })
  .strict();

export const SubmitPayloadSchema = z.object({
  submission_type: z.enum(SUBMISSION_TYPES),
  language: z.string().min(2).max(8),
  body: z.string().min(300).max(40_000),
  facets: facetSchema,
  category_slugs: z.array(z.string().min(1).max(64)).max(12),
  identity_mode: z.enum(IDENTITY_MODES),
  pseudonym: z.string().trim().max(80).optional(),
  author_contact: z.string().trim().email().max(255).optional(),
  consent_publish: z.literal(true),
  consent_redact: z.boolean(),
  consent_translate: z.boolean(),
  translate_all: z.boolean(),
  // anti-abuse
  honeypot: z.string().max(0, "spam"),
  challenge_answer: z.string().trim().min(1).max(40),
  challenge_idx: z.number().int().min(0),
  started_at: z.number().int(),
});

export type SubmitPayload = z.infer<typeof SubmitPayloadSchema>;

/**
 * Simple maritime challenge (§6 — no Google reCAPTCHA). Owner can expand.
 * Question/answer pairs are constant; rotated client-side per session.
 */
export type ChallengeLangCode = "en" | "ru" | "fr" | "es" | "zh" | "ar";
export const CHALLENGES: Array<{
  q: Record<ChallengeLangCode, string>;
  a: string[];
}> = [
  {
    q: {
      en: "How many points of the compass?",
      ru: "Сколько румбов у компаса?",
      fr: "Combien de rumbs comporte la rose des vents ?",
      es: "¿Cuántos rumbos tiene la rosa de los vientos?",
      zh: "罗经有多少个方位点？",
      ar: "كم رمزًا في وردة الرياح (البوصلة)؟",
    },
    a: ["32", "thirty-two", "тридцать два", "trente-deux", "treinta y dos", "三十二", "اثنان وثلاثون", "اثنين وثلاثين"],
  },
  {
    q: {
      en: "Port side light colour?",
      ru: "Цвет ходового огня на левом борту?",
      fr: "Couleur du feu de bâbord ?",
      es: "¿Color de la luz de babor?",
      zh: "左舷航行灯的颜色？",
      ar: "لون فانوس الجانب الأيسر (الميناوي)؟",
    },
    a: ["red", "красный", "rouge", "rojo", "红", "红色", "أحمر"],
  },
  {
    q: {
      en: "Starboard side light colour?",
      ru: "Цвет ходового огня на правом борту?",
      fr: "Couleur du feu de tribord ?",
      es: "¿Color de la luz de estribor?",
      zh: "右舷航行灯的颜色？",
      ar: "لون فانوس الجانب الأيمن (الميامن)؟",
    },
    a: ["green", "зелёный", "зеленый", "vert", "verde", "绿", "绿色", "أخضر"],
  },
  {
    q: {
      en: "International distress call (one word)?",
      ru: "Международный сигнал бедствия (одно слово)?",
      fr: "Appel de détresse international (un mot) ?",
      es: "¿Llamada internacional de socorro (una palabra)?",
      zh: "国际遇险呼救（一个词）？",
      ar: "نداء الاستغاثة الدولي (كلمة واحدة)؟",
    },
    a: ["mayday", "мейдей"],
  },
];